/**
 * Professional Tax (PT) engine — state-wise slabs.
 *
 * Mirrors backend/src/utils/professionalTax.js (kept in sync manually since the
 * backend is CommonJS and the frontend is ES modules), the same way
 * ctcEngine.js is mirrored. The payslip figure is always the backend's — this
 * copy exists so the employee form and payroll screens can preview it without
 * a round trip.
 *
 * ── How a slab is matched ───────────────────────────────────────────────────
 * `salaryBasis` says which salary figure the `from`/`to` range is compared
 * against, derived from each state's filing frequency:
 *   monthly      → monthly gross
 *   half_yearly  → monthly gross x 6   (Kerala, Tamil Nadu — matches statute)
 *   annual       → monthly gross x 12  (Bihar — its 3L-10L ranges are annual)
 * Meghalaya is the one exception: it files annually but its published ranges
 * (0-15,000) are monthly figures, so it carries salaryBasis 'monthly'.
 *
 * ── How the deduction reaches the payslip ───────────────────────────────────
 * PT is deducted every month, never as a lump sum.
 *   monthly states     → the slab amount, each month
 *   half-yearly/annual → annual liability / 12
 * Any shortfall from that division (or a state's statutory annual ceiling that
 * exceeds 12 x monthly, e.g. Karnataka's 200/month but 2,500/year) is trued up
 * in a single month: the state's own special month where it declares one
 * (February for AP, Gujarat, Karnataka, Maharashtra, Puducherry), otherwise
 * March, the last month of the financial year. Twelve months therefore always
 * add up to exactly the annual figure.
 *
 * States absent from this table levy no PT (Delhi, Haryana, UP, Rajasthan,
 * Odisha, Chhattisgarh, ...) and resolve to zero.
 */

const FEBRUARY = 2;
const MARCH = 3;

// slabs: [from, to, amount, annual] — `amount` is per filing period, `annual` is
// the full-year liability for that slab.
const PT_STATES = {
  andhra_pradesh: {
    name: 'Andhra Pradesh', frequency: 'monthly', salaryBasis: 'monthly', specialMonth: FEBRUARY,
    slabs: [[0, 15000, 0, 0], [15001, 20000, 150, 1800], [20001, Infinity, 200, 2500]]
  },
  assam: {
    name: 'Assam', frequency: 'monthly', salaryBasis: 'monthly',
    slabs: [[0, 10000, 0, 0], [10001, 15000, 150, 1800], [15001, Infinity, 208, 2500]]
  },
  bihar: {
    name: 'Bihar', frequency: 'annual', salaryBasis: 'annual',
    slabs: [[0, 300000, 0, 0], [300001, 500000, 1000, 1000], [500001, 1000000, 2000, 2000], [1000001, Infinity, 2500, 2500]]
  },
  gujarat: {
    name: 'Gujarat', frequency: 'monthly', salaryBasis: 'monthly', specialMonth: FEBRUARY,
    slabs: [[0, 12000, 0, 0], [12001, Infinity, 200, 2400]]
  },
  jharkhand: {
    name: 'Jharkhand', frequency: 'monthly', salaryBasis: 'monthly',
    slabs: [[0, 25000, 0, 0], [25001, Infinity, 100, 1200]]
  },
  karnataka: {
    name: 'Karnataka', frequency: 'monthly', salaryBasis: 'monthly', specialMonth: FEBRUARY,
    slabs: [[0, 24999, 0, 0], [25000, Infinity, 200, 2500]]
  },
  kerala: {
    name: 'Kerala', frequency: 'half_yearly', salaryBasis: 'half_yearly',
    slabs: [
      [0, 11999, 0, 0], [12000, 17999, 120, 240], [18000, 29999, 180, 360],
      [30000, 44999, 360, 720], [45000, 59999, 600, 1200], [60000, 74999, 750, 1500],
      [75000, Infinity, 1250, 2500]
    ]
  },
  madhya_pradesh: {
    name: 'Madhya Pradesh', frequency: 'monthly', salaryBasis: 'monthly',
    slabs: [[0, 18750, 0, 0], [18751, 25000, 125, 1500], [25001, 33333, 166, 1992], [33334, Infinity, 208, 2500]]
  },
  maharashtra: {
    name: 'Maharashtra', frequency: 'monthly', salaryBasis: 'monthly', specialMonth: FEBRUARY,
    genderSlabs: {
      male: [[0, 7500, 0, 0], [7501, 10000, 175, 2100], [10001, Infinity, 200, 2500]],
      female: [[0, 25000, 0, 0], [25001, Infinity, 200, 2500]]
    }
  },
  manipur: {
    name: 'Manipur', frequency: 'monthly', salaryBasis: 'monthly',
    slabs: [[0, Infinity, 208, 2500]]
  },
  meghalaya: {
    // Files annually, but the published ranges are monthly salary figures.
    name: 'Meghalaya', frequency: 'annual', salaryBasis: 'monthly',
    slabs: [[0, 4999, 0, 0], [5000, 7499, 175, 175], [7500, 9999, 325, 325], [10000, 14999, 975, 975], [15000, Infinity, 2500, 2500]]
  },
  mizoram: {
    name: 'Mizoram', frequency: 'monthly', salaryBasis: 'monthly',
    slabs: [[0, 5000, 0, 0], [5001, Infinity, 208, 2500]]
  },
  nagaland: {
    name: 'Nagaland', frequency: 'monthly', salaryBasis: 'monthly',
    slabs: [[0, 5000, 0, 0], [5001, Infinity, 208, 2500]]
  },
  puducherry: {
    name: 'Puducherry', frequency: 'monthly', salaryBasis: 'monthly', specialMonth: FEBRUARY,
    slabs: [[0, 10000, 0, 0], [10001, 15000, 100, 1200], [15001, Infinity, 200, 2400]]
  },
  punjab: {
    name: 'Punjab', frequency: 'monthly', salaryBasis: 'monthly',
    slabs: [[0, 24999, 0, 0], [25000, Infinity, 200, 2400]]
  },
  sikkim: {
    name: 'Sikkim', frequency: 'monthly', salaryBasis: 'monthly',
    slabs: [[0, 20000, 0, 0], [20001, 30000, 125, 1500], [30001, 40000, 150, 1800], [40001, Infinity, 200, 2400]]
  },
  tamil_nadu: {
    name: 'Tamil Nadu', frequency: 'half_yearly', salaryBasis: 'half_yearly',
    slabs: [
      [0, 21000, 0, 0], [21001, 30000, 135, 270], [30001, 45000, 315, 630],
      [45001, 60000, 690, 1380], [60001, 75000, 1025, 2050], [75001, Infinity, 1250, 2500]
    ]
  },
  telangana: {
    name: 'Telangana', frequency: 'monthly', salaryBasis: 'monthly',
    slabs: [[0, 15000, 0, 0], [15001, 20000, 150, 1800], [20001, Infinity, 200, 2400]]
  },
  tripura: {
    name: 'Tripura', frequency: 'monthly', salaryBasis: 'monthly',
    slabs: [[0, 7500, 0, 0], [7501, 15000, 100, 1200], [15001, 25000, 150, 1800], [25001, Infinity, 200, 2400]]
  },
  west_bengal: {
    name: 'West Bengal', frequency: 'monthly', salaryBasis: 'monthly',
    slabs: [[0, 10000, 0, 0], [10001, 15000, 110, 1320], [15001, 25000, 130, 1560], [25001, 40000, 150, 1800], [40001, Infinity, 200, 2400]]
  }
};

// Every state/UT the employee form offers, so the dropdown can mark the ones
// that levy no PT instead of silently returning zero.
const PT_STATE_CODES = [
  'andhra_pradesh', 'arunachal_pradesh', 'assam', 'bihar', 'chhattisgarh', 'goa', 'gujarat',
  'haryana', 'himachal_pradesh', 'jharkhand', 'karnataka', 'kerala', 'madhya_pradesh',
  'maharashtra', 'manipur', 'meghalaya', 'mizoram', 'nagaland', 'odisha', 'punjab',
  'rajasthan', 'sikkim', 'tamil_nadu', 'telangana', 'tripura', 'uttar_pradesh', 'uttarakhand',
  'west_bengal', 'delhi', 'jammu_kashmir', 'ladakh', 'chandigarh', 'puducherry'
];

const NON_PT_STATE_NAMES = {
  arunachal_pradesh: 'Arunachal Pradesh', chhattisgarh: 'Chhattisgarh', goa: 'Goa',
  haryana: 'Haryana', himachal_pradesh: 'Himachal Pradesh', odisha: 'Odisha',
  rajasthan: 'Rajasthan', uttar_pradesh: 'Uttar Pradesh', uttarakhand: 'Uttarakhand',
  delhi: 'Delhi', jammu_kashmir: 'Jammu & Kashmir', ladakh: 'Ladakh', chandigarh: 'Chandigarh'
};

const FREQUENCY_LABELS = { monthly: 'Monthly', half_yearly: 'Half-Yearly', annual: 'Annual' };

const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/** Options for a state dropdown, PT-levying states first. */
const getPTStateOptions = () => PT_STATE_CODES
  .map((code) => ({
    code,
    name: PT_STATES[code] ? PT_STATES[code].name : (NON_PT_STATE_NAMES[code] || code),
    levied: Boolean(PT_STATES[code]),
    frequency: PT_STATES[code] ? PT_STATES[code].frequency : null
  }))
  .sort((a, b) => (a.levied === b.levied ? a.name.localeCompare(b.name) : (a.levied ? -1 : 1)));

/** Slabs for a state, resolving the gendered table where a state has one. */
const getPTSlabs = (stateCode, gender) => {
  const state = PT_STATES[stateCode];
  if (!state) return [];
  if (!state.genderSlabs) return state.slabs;
  // Maharashtra is the only gendered state. Anything other than an explicit
  // 'female' falls back to the male table, which is the stricter of the two.
  return gender === 'female' ? state.genderSlabs.female : state.genderSlabs.male;
};

/** The salary figure a state's slab ranges are compared against. */
const getEvaluatedSalary = (monthlyGross, salaryBasis) => {
  if (salaryBasis === 'annual') return monthlyGross * 12;
  if (salaryBasis === 'half_yearly') return monthlyGross * 6;
  return monthlyGross;
};

/**
 * Resolve the PT deduction for one month.
 *
 * @param {Object}  args
 * @param {String}  args.state         State code, e.g. 'karnataka'
 * @param {Number}  args.monthlyGross  Monthly gross salary
 * @param {String} [args.gender]       'male' | 'female' | 'other' — Maharashtra only
 * @param {Number} [args.month]        Calendar month 1-12; omit for the plain monthly figure
 * @returns {Object} Full breakdown; `amount` is what to deduct this month.
 */
const calculateProfessionalTax = ({ state, monthlyGross, gender, month } = {}) => {
  const gross = Math.max(Number(monthlyGross) || 0, 0);
  const stateCode = state || '';
  const config = PT_STATES[stateCode];

  const base = {
    state: stateCode,
    stateName: config ? config.name : (NON_PT_STATE_NAMES[stateCode] || ''),
    frequency: config ? config.frequency : null,
    frequencyLabel: config ? FREQUENCY_LABELS[config.frequency] : null,
    salaryBasis: config ? config.salaryBasis : null,
    evaluatedSalary: 0,
    slab: null,
    slabAmount: 0,
    annualAmount: 0,
    baseMonthlyAmount: 0,
    trueUpMonth: null,
    isTrueUpMonth: false,
    amount: 0
  };

  if (!stateCode) {
    return { ...base, applicable: false, reason: 'No professional tax state configured' };
  }
  if (!config) {
    return {
      ...base,
      applicable: false,
      reason: `${base.stateName || 'This state'} does not levy professional tax`
    };
  }

  const evaluatedSalary = getEvaluatedSalary(gross, config.salaryBasis);
  const slabs = getPTSlabs(stateCode, gender);
  const matched = slabs.find(([from, to]) => evaluatedSalary >= from && evaluatedSalary <= to);

  if (!matched) {
    return { ...base, evaluatedSalary, applicable: false, reason: 'No matching slab' };
  }

  const [from, to, slabAmount, annualAmount] = matched;

  // Spread the annual liability across all twelve months, then true up the
  // rounding remainder (and any annual ceiling above 12 x monthly) in one month.
  const baseMonthlyAmount = config.frequency === 'monthly'
    ? slabAmount
    : Math.round(annualAmount / 12);
  const remainder = annualAmount - (baseMonthlyAmount * 12);
  const trueUpMonth = remainder === 0 ? null : (config.specialMonth || MARCH);
  const isTrueUpMonth = trueUpMonth !== null && Number(month) === trueUpMonth;
  const amount = isTrueUpMonth ? annualAmount - (baseMonthlyAmount * 11) : baseMonthlyAmount;

  return {
    ...base,
    applicable: true,
    reason: null,
    evaluatedSalary,
    slab: { from, to, amount: slabAmount, annual: annualAmount },
    slabAmount,
    annualAmount,
    baseMonthlyAmount,
    trueUpMonth,
    trueUpMonthName: trueUpMonth ? MONTH_NAMES[trueUpMonth] : null,
    isTrueUpMonth,
    amount
  };
};

/**
 * Decide whether PT applies to an employee and under which state, combining the
 * employee's own statutory settings with the client's global payroll settings.
 *
 * Client settings act as follows:
 *   professionalTax.enabled = false      → PT off for every employee (kill switch)
 *   professionalTax.defaultState         → used when the employee has no state of their own
 *   professionalTax.overrideEmployeeState → force every employee onto defaultState
 */
const resolvePTContext = (employee, client) => {
  const clientPT = (client && client.payrollSettings && client.payrollSettings.professionalTax) || {};
  const statutory = (employee && employee.statutory) || {};

  if (clientPT.enabled === false) {
    return { enabled: false, state: '', gender: employee && employee.gender, reason: 'Professional tax is disabled in client payroll settings' };
  }
  if (statutory.ptEnabled !== true) {
    return { enabled: false, state: '', gender: employee && employee.gender, reason: 'Professional tax is not enabled for this employee' };
  }

  const employeeState = (statutory.pt && statutory.pt.state) || '';
  const state = clientPT.overrideEmployeeState
    ? (clientPT.defaultState || '')
    : (employeeState || clientPT.defaultState || '');

  return {
    enabled: true,
    state,
    gender: employee && employee.gender,
    source: clientPT.overrideEmployeeState ? 'client_override' : (employeeState ? 'employee' : 'client_default'),
    reason: null
  };
};

/**
 * PT deduction for an employee in a given month. Returns the same breakdown as
 * calculateProfessionalTax, with `amount` zero whenever PT does not apply.
 */
const calculateEmployeePT = (employee, client, monthlyGross, month) => {
  const context = resolvePTContext(employee, client);
  if (!context.enabled) {
    return {
      applicable: false,
      amount: 0,
      annualAmount: 0,
      state: '',
      stateName: '',
      reason: context.reason,
      source: context.source || null
    };
  }
  const result = calculateProfessionalTax({
    state: context.state,
    monthlyGross,
    gender: context.gender,
    month
  });
  return { ...result, source: context.source };
};

export {
  PT_STATES,
  PT_STATE_CODES,
  NON_PT_STATE_NAMES,
  FREQUENCY_LABELS,
  getPTStateOptions,
  getPTSlabs,
  calculateProfessionalTax,
  resolvePTContext,
  calculateEmployeePT
};
